import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@mlm/ui';
import { exchangeImpersonationToken } from '../lib/api';

/**
 * Landing route for the admin's "Login as user" flow into Creator Studio. The
 * admin SPA opens this tab with `?token=…`; we exchange the grant for a real
 * session (which sets the impersonation refresh cookie on this origin) and
 * forward to the dashboard. The grant is single-use + short-lived, so this
 * page can't be bookmarked or replayed.
 */
export function ImpersonatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const tokenRef = useRef(searchParams.get('token'));
  // Guard against React 18 StrictMode double-mount eating the single-use token.
  const exchangedRef = useRef(false);

  useEffect(() => {
    if (exchangedRef.current) return;
    exchangedRef.current = true;
    const token = tokenRef.current;
    if (!token) {
      setError('Missing impersonation token.');
      return;
    }
    void (async () => {
      try {
        await exchangeImpersonationToken(token);
        navigate('/', { replace: true });
      } catch (err) {
        const code = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
        setError(
          code === 'invalid_or_expired_token'
            ? 'This impersonation link has expired or has already been used.'
            : 'Could not start impersonation session.',
        );
      }
    })();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardContent className="pt-6">
          {error ? (
            <p className="text-sm text-destructive">{error}</p>
          ) : (
            <p className="text-sm text-muted-foreground">Starting impersonation session…</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
